// "Mitä uutta" (Axel 2026-10-14, docs/design/SANAPAJA.md "Alongside → 'Mitä uutta' walkthrough on the next app
// open"): the steps of the tour that shows the rising word pace and the grammar read twice, and reminds a person
// whose notifications are off on this device, or who has been leaving out the day's cards, of them. Steps 4 and 5
// go by data, never by name: the copy is the same for whoever sees them (i18n/areas/whatsNew.ts).
//
//   1 pace       Tänään's rate line ([data-rate-line]: "6 uutta sanaa päivässä · seuraava nousu 25.12.") and the
//                staircase in one line (the interval from paceSteps, never a fixed number)   — while the course runs
//   2 why        the same line: new words get easier as the grammar and the known words carry them
//   3 grammar    Tänään's "Kertaa viikon kielioppi" door when it stands there now, else the week page's
//                "Kielioppi luettu N/2" (/lessons): from which date this week (secondReadingFrom)  — a week with
//                Kielioppi topics only
//   4 push       only when this device's reminders are off, blocked or need the Home Screen app (lib/push.ts; not
//                for a browser without notifications or an iOS too old for them); "off" carries the button that
//                turns them on
//   5 practice   only when the day's cards were left out on most of the last 7 days (engine pedagogy/practice-gap.ts):
//                Harjoitus on Tänään and what it takes
// Pure: the host (WhatsNewHost.tsx) gathers the facts, WhatsNewTour.tsx writes the captions.
import type { PracticeGap } from '@valyrio/engine/pedagogy/practice-gap';
import type { PaceStep } from '@valyrio/engine/course';
import type { PushState } from '../../lib/push';
import type { SecondReading } from '../../grammartwice/state';

/** The tour's id in the person's one-time explainers (PATCH /api/me { introSeen }: user.intros_seen, on any device). */
export const WHATS_NEW_SEEN = 'tour-whats-new-2026-10';
/** The replay request's id (Asetukset → "Näytä esittely uudelleen"). */
export const WHATS_NEW_TOUR = 'whats-new';

/** Tänään's path and the week page's. */
export const TODAY_PATH = '/';
export const WEEK_PATH = '/lessons';

/** What the daily cards take, minutes (SANAPAJA.md "~10–15 min"; the app has no estimate of its own for the day's deck). */
export const PRACTICE_MINUTES = { from: 10, to: 15 } as const;

/** What each step points at, in the order tried (the first one on the screen wins; none: the caption alone). */
export const WHATS_NEW_TARGETS = {
  // the rate line under the course row (phone outside a session, PC always); in a phone session the progress line's
  // "Viikko N ›", whose sheet holds it
  pace: ['[data-rate-line]', '[data-week-row]'],
  door: ['[data-tour="grammar-second"]'],
  week: ['.wk-now [data-tour="grammar-read"]', '[data-tour="grammar-read"]'],
  // the week's Harjoitus: the PC margin ledger's step, the phone week row's ribbon, else the deck's card on the screen
  practice: ['.wl-step[data-step="practice"]', '[data-week-row] [data-step="practice"]', '.study .card', '[data-week-row]'],
} as const;

export interface WhatsNewFacts {
  today: string;
  /** Tänään's rate line as it reads now (null before the course starts). */
  rateLine: string | null;
  /** The rate's next rise, or null (the top stair, a flat pace after its first month). */
  nextRise: { date: string; due: boolean } | null;
  /** The pace's step table (paceSteps). */
  steps: readonly PaceStep[];
  /** This week's two readings (null: before the course, or a week without Kielioppi topics). */
  reading: SecondReading | null;
  /** Tänään's "Kertaa viikon kielioppi" door is on the screen now. */
  doorOnScreen: boolean;
  /** This device's reminders (null: not known). */
  push: PushState | null;
  /** The last week's day's cards (null: not known, e.g. offline). */
  practice: PracticeGap | null;
}

/** The grammar step's case. */
export type GrammarCase = 'door' | 'soon' | 'waitsFirst' | 'later' | 'done';

export type WhatsNewStep =
  | { kind: 'pace'; route: string; targets: readonly string[]; line: string; everyDays: number | null; top: boolean }
  | { kind: 'why'; route: string; targets: readonly string[] }
  | { kind: 'grammar'; route: string; targets: readonly string[]; case: GrammarCase; from: string }
  | { kind: 'push'; route: string; state: 'off' | 'denied' | 'needs-install' }
  | { kind: 'practice'; route: string; targets: readonly string[] };

/** The push states the reminder step speaks to (the rest: nothing the person can do here, or already on). */
const PUSH_STEP: readonly PushState[] = ['off', 'denied', 'needs-install'];

/**
 * The staircase's average stair, in days (from the stairs' first days), null without a staircase of at least two
 * stairs. The default 6 → 12 to 24.9.2027: 7 rises over 320 days, ≈ 46 days ("noin puolentoista kuukauden välein").
 */
export function stairDays(steps: readonly PaceStep[]): number | null {
  const stairs = steps.filter((s) => s.step >= 0);
  if (stairs.length < 2) return null;
  const first = Date.parse(stairs[0]!.from);
  const last = Date.parse(stairs[stairs.length - 1]!.from);
  const days = Math.round((last - first) / 86_400_000 / (stairs.length - 1));
  return days > 0 ? days : null;
}

/** The grammar step: the door on Tänään when it stands there, else the week page and when the second reading comes. */
export function grammarCase(r: SecondReading, today: string, doorOnScreen: boolean): GrammarCase {
  if (r.me.second) return 'done';
  if (r.offered) return doorOnScreen ? 'door' : 'later';
  if (r.due) return 'waitsFirst';
  return r.from > today ? 'soon' : 'waitsFirst';
}

/** The tour's steps for these facts, in order. */
export function whatsNewSteps(f: WhatsNewFacts): WhatsNewStep[] {
  const out: WhatsNewStep[] = [];
  if (f.rateLine) {
    out.push({
      kind: 'pace',
      route: TODAY_PATH,
      targets: WHATS_NEW_TARGETS.pace,
      line: f.rateLine,
      everyDays: f.nextRise ? stairDays(f.steps) : null,
      top: !f.nextRise && f.steps.some((s) => s.step > 0),
    });
    out.push({ kind: 'why', route: TODAY_PATH, targets: WHATS_NEW_TARGETS.pace });
  }
  if (f.reading) {
    const c = grammarCase(f.reading, f.today, f.doorOnScreen);
    out.push(
      c === 'door'
        ? { kind: 'grammar', route: TODAY_PATH, targets: WHATS_NEW_TARGETS.door, case: c, from: f.reading.from }
        : { kind: 'grammar', route: WEEK_PATH, targets: WHATS_NEW_TARGETS.week, case: c, from: f.reading.from },
    );
  }
  if (f.push && PUSH_STEP.includes(f.push)) {
    out.push({ kind: 'push', route: TODAY_PATH, state: f.push as 'off' | 'denied' | 'needs-install' });
  }
  if (f.practice?.gap) out.push({ kind: 'practice', route: TODAY_PATH, targets: WHATS_NEW_TARGETS.practice });
  return out;
}
